import * as React from "react";
import {useEffect} from "react";
import {useFilePicker} from "use-file-picker";
import {Button, Flex, FormField, FormLabel, FormMessage, Text, ZoomToFitIcon} from "@fluentui/react-northstar";
import * as imageConversion from 'image-conversion';
import languageObject from "../../translations";

interface ImagePickerProps {
    handleFileSelection: (name: string, content: string, width: number, height: number, extension: string) => void,
    onCrop: () => void,
    onLoading: () => void
}

/**
 * File picker
 */
function ImagePicker(props: ImagePickerProps) {
    /**
     * Retrieve handleFileSelection method as const from props
     * to avoid warning in useEffect
     */
    const {handleFileSelection} = props;

    /**
     * File picker options
     */
    const {openFilePicker, filesContent, errors, loading} = useFilePicker({
        multiple: false,
        readAs: 'DataURL',
        accept: 'image/*',
    });

    /**
     * Handle file selection
     */
    const handleSelection = async () => {
        // Optimize image
        const imageData = await optimizeImage(filesContent[0].content as string);
        handleFileSelection(filesContent[0].name, imageData.content, imageData.width, imageData.height, imageData.extension);
    }

    /**
     * Optimize image
     */
    const optimizeImage = (content: string): Promise<{
        content: string,
        width: number,
        height: number,
        extension: string
    }> => {
        return new Promise<{ content: string, width: number, height: number, extension: string }>((resolve, reject) => {
            let canvas = document.createElement("canvas");
            let ctx = canvas.getContext("2d");
            let img = new Image();
            img.onload = async () => {
                // Resize height if more than 1024px
                if (img.height > 1024) {
                    img.width = (1024 * img.width) / img.height;
                    img.height = 1024;
                }

                // Resize width if more than 1024px
                if (img.width > 1024) {
                    img.height = (1024 * img.height) / img.width;
                    img.width = 1024;
                }

                // Draw image on canvas
                canvas.width = img.width;
                canvas.height = img.height;
                ctx?.drawImage(img, 0, 0, img.width, img.height);

                // File content type
                let contentType = content.split("data:")[1].split(";base64,")[0];

                // Keep clear background depending on image format
                switch (contentType) {
                    case "image/webp":
                    case "image/gif":
                    case "image/svg+xml":
                    case "image/png":
                        contentType = "image/png";
                        break;
                    default:
                        contentType = "image/jpeg"
                        break;
                }

                // Convert canvas to blob
                canvas.toBlob(async (blob) => {
                    if (!blob) reject("Blob image is undefined");
                    else {
                        // Blob size more than 1Mo
                        if (blob.size > 1000000)
                            // Compress blob to 1Mo max
                            blob = await imageConversion.compressAccurately(blob, 1000);
                        // Return blob data url
                        resolve({
                            content: await imageConversion.filetoDataURL(blob),
                            width: img.width,
                            height: img.height,
                            extension: contentType.replace("image/", "")
                        })
                    }
                }, contentType);
            };
            img.src = content;
        });
    };

    /**
     * On update
     */
    useEffect(() => {
        // Loading file
        if (loading) props.onLoading();
        // A file is selected
        if (filesContent.length === 1) handleSelection();
    }, [filesContent, loading]);

    return (
        <FormField className={"form-input"}>
            <FormLabel>
                {languageObject.translate("Image")}<Text color={"red"}> *</Text>
            </FormLabel>
            <Flex gap={"gap.small"}>
                <Button fluid onClick={openFilePicker}
                        tinted={filesContent.length === 1}
                        content={filesContent.length === 0 ? languageObject.translate("SelectImage") : filesContent[0].name}/>
                {filesContent.length === 1 &&
                    <Button icon={<ZoomToFitIcon outline/>} styles={{minWidth: "auto"}}
                            content={languageObject.translate("Crop")}
                            onClick={props.onCrop}/>
                }
            </Flex>
            <FormMessage role="alert" error>
                {!!errors[0] && languageObject.translate("InvalidImage")}
            </FormMessage>
        </FormField>
    );
}

export default ImagePicker